import { AppConstants } from '@/helpers/app.constants';
import { CertificationHelper } from '@/helpers/certification.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  CertificationType,
  CertificationsService,
  Document,
  PropertiesService,
  PropertyCategory,
  RealProperty,
  VaultService
} from '@eemo/api-client';
import { environment } from 'src/environments/environment';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { NgTemplateOutlet, NgIf, NgFor } from '@angular/common';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';

export class PropertyCertifyDialogData {
  prop: RealProperty;
}

@Component({
    selector: 'app-property-certify-dialog',
    templateUrl: './property-certify-dialog.component.html',
    styleUrls: ['./property-certify-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, MatDialogModule, NgIf, MatButtonModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, NgFor, MatOptionModule, MatCheckboxModule]
})
export class PropertyCertifyDialogComponent implements OnInit {
  @ViewChildren(MatCheckbox) docsCheckboxes: QueryList<MatCheckbox>;

  certifyForm: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;
  docsAvailable: Document[] = [];
  readyForCertification = true;
  missingFieldsMessage = '';
  UserHelper = UserHelper;
  renew = false;

  professionalCertificationOnly = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyCertifyDialogData,
    private certifService: CertificationsService,
    private vaultService: VaultService,
    private propService: PropertiesService,
    private dictCacheService: DictionaryCacheService,
    public propHelper: PropertyHelper,
    public userHelper: UserHelper,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PropertyCertifyDialogComponent>,
    public certificationHelper: CertificationHelper,
    private router: Router
  ) {
    this.renew = this.data.prop.certified;
    if (this.renew) {
      this.dialogTitle = 'Renouveler la certification';
    } else {
      this.dialogTitle = 'Certifier le bien';
    }
    // if the user is real estate and is a property owner, the certification professionel is the only available
    this.professionalCertificationOnly = this.userHelper.isRealEstate();
  }

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.certifyForm.get(field);
  }

  get docsFormArray() {
    return this.certifyForm.controls.docs as UntypedFormArray;
  }

  getCertificationTypes(): CertificationType[] {
    return [CertificationType.OWNER, CertificationType.PROFESSIONAL];
  }

  isTypeAllowed(type: CertificationType): boolean {
    if (type === CertificationType.PROFESSIONAL) {
      return this.data.prop.certificationType !== CertificationType.OWNER;
    }
    return true;
  }

  ngOnInit() {
    this.certifyForm = this.fb.group({
      type: [
        this.professionalCertificationOnly
          ? CertificationType.PROFESSIONAL
          : this.data.prop.certificationType ?? CertificationType.OWNER,
        Validators.required
      ],
      withMaintenances: [false],
      syncOpenData: [true],
      docs: new UntypedFormArray([])
    });
    this.vaultService.getRealPropertyDocuments(this.data.prop.uid).subscribe((data) => {
      const docs = data.filter((d) => !d.auto);
      this.docsAvailable = docs.sort((a, b) => {
        const categories = Object.keys(PropertyCategory);
        const categoryOrderA = categories.indexOf(a.category);
        const categoryOrderB = categories.indexOf(b.category);

        if (categoryOrderA > categoryOrderB) {
          return 1;
        }
        if (categoryOrderA < categoryOrderB) {
          return -1;
        }
        return 0;
      });
      docs.forEach((doc) => this.docsFormArray.push(new UntypedFormControl(doc.certified)));
    });
    this.checkCertificationRequiredField(this.data.prop);
  }

  onSubmit() {
    if (this.certifyForm.valid) {
      this.formSubmitting = true;
      const withMaintenances = this.professionalCertificationOnly ? false : this.f('withMaintenances').value;
      const syncOpenData = this.f('syncOpenData').value;
      const selectedDocsIds = this.certifyForm.value.docs
        .map((checked, i) => (checked ? this.docsAvailable[i].uid : null))
        .filter((v) => v !== null);
      if (this.renew) {
        this.certifService
          .renewRealPropertyCertification(this.data.prop.uid, {
            type: this.f('type').value,
            withMaintenances,
            syncOpenData,
            docs: selectedDocsIds
          })
          .subscribe(
            (result) => {
              this.dialogRef.close(true);
              this.alertService.success('Renouvellement effectué');
            },
            (err: HttpErrorResponse) => {
              this.dialogRef.close(false);
            }
          );
      } else {
        this.certifService
          .certifyRealProperty(this.data.prop.uid, {
            type: this.f('type').value,
            withMaintenances,
            syncOpenData,
            docs: selectedDocsIds
          })
          .subscribe(
            (result) => {
              this.dialogRef.close(true);
              this.alertService.success('Certification effectuée');
            },
            (err: HttpErrorResponse) => {
              this.dialogRef.close(false);
            }
          );
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }

  checkCertificationRequiredField(prop: RealProperty) {
    const missingFields: string[] = [];
    const requiredAttrs = this.dictCacheService.getAttributesRequiredForCertification(prop.type);
    this.readyForCertification = true;
    this.propService.getRealPropertyAttributes(prop.uid).subscribe((propAttrs) => {
      // check property fields
      if (!prop.location) {
        this.readyForCertification = false;
        missingFields.push('Localisation');
      }
      if (!prop.address) {
        this.readyForCertification = false;
        missingFields.push('Adresse');
      }
      if (this.f('type').value === CertificationType.OWNER) {
        if (!prop.landRegistry || !prop.landRegistry.plot) {
          this.readyForCertification = false;
          missingFields.push('Cadastre');
        }
        if (propAttrs.filter((p) => p.code === environment.BUILDING_YEAR_CODE).length === 0) {
          this.readyForCertification = false;
          missingFields.push('Année de construction');
        }
      }
      // check attributes
      requiredAttrs.forEach((reqAttr) => {
        const curAttr = propAttrs.find((a) => a.code === reqAttr.code);
        if (!curAttr || !curAttr.value) {
          this.readyForCertification = false;
          missingFields.push(reqAttr.display.name);
        }
      });
      this.missingFieldsMessage = missingFields.join(', ');
    });
  }

  toggleCheckAll(change: MatCheckboxChange) {
    this.docsFormArray.controls.forEach((element) => {
      element.setValue(change.checked);
    });
  }

  docCheckBoxEvent(change: MatCheckboxChange) {
    this.docsCheckboxes.find((check) => check.name === 'selectAll').checked = false;
  }

  goToBuildingPage() {
    const queryParams = {};
    queryParams[AppConstants.PARAM_EDIT_GROUP_CODE] = 'CLASSIFICATION';
    this.router.navigate(['property', this.data.prop.uid, 'building'], { queryParams });
    this.onNoClick();
  }
}
